import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import "@/css/pool.css";
import { useAuth } from "@/shared/hooks";
import {
  fetchPoolRoomState,
  submitPoolShot,
  completePoolTurn,
  completePoolMatch
} from "@/shared/api";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  poolStateHydrated,
  selectPoolError,
  selectPoolRoomState
} from "@/store/slices/poolSlice";
import { selectPoolTournamentState } from "@/store/slices/poolTournamentSlice";
import { PoolSessionState, selectPoolSessionState } from "@/store/slices/poolSessionSlice";
import type { PoolBallState } from "@/shared/types/pool";

type Ball = {
  id: number;
  number: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  striped: boolean;
  isCue: boolean;
  pocketed: boolean;
  spinX: number;
  spinY: number;
  sinking: boolean;
  sinkProgress: number;
  sinkPocket: Vec2 | null;
};

type Vec2 = {
  x: number;
  y: number;
};

type ShotAssistMode = "off" | "basic" | "full";

type AssistHit = {
  point: Vec2;
  kind: "ball" | "rail" | "pocket";
  targetBall?: Ball;
};

type DragState = {
  pointerId: number;
  start: Vec2;
  direction: Vec2;
};

const WORLD_WIDTH = 1000;
const WORLD_HEIGHT = 560;
const RAIL = 42;
const CUSHION_INSET = 42;
const FELT_INSET = 40;
// Kept for shot-assist rail preview bounds; gameplay collisions use pad polygons below.
const COLLISION_RAIL = 56;
const BALL_RADIUS = 14;
const POCKET_RADIUS = 29;
const STOP_EPSILON = 1.7;
const MAX_SPEED = 1100;
const MIN_SHOT_POWER = 14;
const BALL_RESTITUTION = 0.9;
const RAIL_RESTITUTION = 0.89;

const SOLID_COLORS = [
  "#f4c542",
  "#2449d8",
  "#e24b39",
  "#7b2ab8",
  "#ff8c1a",
  "#239a4b",
  "#8a241f"
];

const STRIPE_COLORS = [
  "#f4c542",
  "#2449d8",
  "#e24b39",
  "#7b2ab8",
  "#ff8c1a",
  "#239a4b",
  "#8a241f"
];

function ballGroup(number: number): "solids" | "stripes" | "other" {
  if (number >= 1 && number <= 7) return "solids";
  if (number >= 9 && number <= 15) return "stripes";
  return "other";
}

const POCKETS: Vec2[] = [
  { x: RAIL + 11, y: RAIL + 11 },
  { x: WORLD_WIDTH / 2, y: RAIL - 2 },
  { x: WORLD_WIDTH - RAIL - 11, y: RAIL + 11 },
  { x: RAIL + 11, y: WORLD_HEIGHT - RAIL - 11 },
  { x: WORLD_WIDTH / 2, y: WORLD_HEIGHT - RAIL + 2 },
  { x: WORLD_WIDTH - RAIL - 11, y: WORLD_HEIGHT - RAIL - 11 }
];

const PAD_POLYGONS: Vec2[][] = [
  [
    { x: CUSHION_INSET + 18, y: CUSHION_INSET - 2 },
    { x: WORLD_WIDTH / 2 - 17, y: CUSHION_INSET - 2 },
    { x: WORLD_WIDTH / 2 - 25, y: FELT_INSET + 18 },
    { x: FELT_INSET + 40, y: FELT_INSET + 18 }
  ],
  [
    { x: WORLD_WIDTH / 2 + 17, y: CUSHION_INSET - 2 },
    { x: WORLD_WIDTH - CUSHION_INSET - 18, y: CUSHION_INSET - 2 },
    { x: WORLD_WIDTH - FELT_INSET - 40, y: FELT_INSET + 18 },
    { x: WORLD_WIDTH / 2 + 25, y: FELT_INSET + 18 }
  ],
  [
    { x: FELT_INSET + 42, y: WORLD_HEIGHT - FELT_INSET - 18 },
    { x: WORLD_WIDTH / 2 - 23, y: WORLD_HEIGHT - FELT_INSET - 18 },
    { x: WORLD_WIDTH / 2 - 15, y: WORLD_HEIGHT - CUSHION_INSET + 2 },
    { x: CUSHION_INSET + 20, y: WORLD_HEIGHT - CUSHION_INSET + 2 }
  ],
  [
    { x: WORLD_WIDTH / 2 + 23, y: WORLD_HEIGHT - FELT_INSET - 18 },
    { x: WORLD_WIDTH - FELT_INSET - 42, y: WORLD_HEIGHT - FELT_INSET - 18 },
    { x: WORLD_WIDTH - CUSHION_INSET - 20, y: WORLD_HEIGHT - CUSHION_INSET + 2 },
    { x: WORLD_WIDTH / 2 + 15, y: WORLD_HEIGHT - CUSHION_INSET + 2 }
  ],
  [
    { x: CUSHION_INSET - 2, y: CUSHION_INSET + 24 },
    { x: FELT_INSET + 18, y: FELT_INSET + 44 },
    { x: FELT_INSET + 18, y: WORLD_HEIGHT - FELT_INSET - 44 },
    { x: CUSHION_INSET - 2, y: WORLD_HEIGHT - CUSHION_INSET - 24 }
  ],
  [
    { x: WORLD_WIDTH - FELT_INSET - 18, y: FELT_INSET + 44 },
    { x: WORLD_WIDTH - CUSHION_INSET + 2, y: CUSHION_INSET + 24 },
    { x: WORLD_WIDTH - CUSHION_INSET + 2, y: WORLD_HEIGHT - CUSHION_INSET - 24 },
    { x: WORLD_WIDTH - FELT_INSET - 18, y: WORLD_HEIGHT - FELT_INSET - 44 }
  ]
];

const PAD_SEGMENTS: Array<{ a: Vec2; b: Vec2 }> = PAD_POLYGONS.flatMap((poly) =>
  poly.map((point, idx) => ({ a: point, b: poly[(idx + 1) % poly.length] }))
);

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function magnitude(v: Vec2): number {
  return Math.hypot(v.x, v.y);
}

function normalize(v: Vec2): Vec2 {
  const len = magnitude(v);
  if (len <= 0.0001) {
    return { x: 0, y: 0 };
  }

  return { x: v.x / len, y: v.y / len };
}

function closestPointOnSegment(p: Vec2, a: Vec2, b: Vec2): Vec2 {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const abLenSq = abx * abx + aby * aby;
  if (abLenSq <= 0.000001) {
    return a;
  }
  const t = clamp(((p.x - a.x) * abx + (p.y - a.y) * aby) / abLenSq, 0, 1);
  return { x: a.x + abx * t, y: a.y + aby * t };
}

function rayCircleHit(origin: Vec2, dir: Vec2, center: Vec2, radius: number): number | null {
  const dx = origin.x - center.x;
  const dy = origin.y - center.y;
  const b = 2 * (dx * dir.x + dy * dir.y);
  const c = dx * dx + dy * dy - radius * radius;
  const disc = b * b - 4 * c;
  if (disc < 0) return null;
  const sqrtDisc = Math.sqrt(disc);
  const t1 = (-b - sqrtDisc) / 2;
  const t2 = (-b + sqrtDisc) / 2;
  if (t1 > 0.0001) return t1;
  if (t2 > 0.0001) return t2;
  return null;
}

function computeAssistHit(cueBall: Ball, dir: Vec2, balls: Ball[]): AssistHit | null {
  let bestT = Number.POSITIVE_INFINITY;
  let hit: AssistHit | null = null;

  const minX = COLLISION_RAIL + BALL_RADIUS;
  const maxX = WORLD_WIDTH - COLLISION_RAIL - BALL_RADIUS;
  const minY = COLLISION_RAIL + BALL_RADIUS;
  const maxY = WORLD_HEIGHT - COLLISION_RAIL - BALL_RADIUS;

  if (Math.abs(dir.x) > 0.0001) {
    const txLeft = (minX - cueBall.x) / dir.x;
    const yLeft = cueBall.y + txLeft * dir.y;
    if (txLeft > 0.0001 && yLeft >= minY && yLeft <= maxY && txLeft < bestT) {
      bestT = txLeft;
      hit = { point: { x: minX, y: yLeft }, kind: "rail" };
    }
    const txRight = (maxX - cueBall.x) / dir.x;
    const yRight = cueBall.y + txRight * dir.y;
    if (txRight > 0.0001 && yRight >= minY && yRight <= maxY && txRight < bestT) {
      bestT = txRight;
      hit = { point: { x: maxX, y: yRight }, kind: "rail" };
    }
  }

  if (Math.abs(dir.y) > 0.0001) {
    const tyTop = (minY - cueBall.y) / dir.y;
    const xTop = cueBall.x + tyTop * dir.x;
    if (tyTop > 0.0001 && xTop >= minX && xTop <= maxX && tyTop < bestT) {
      bestT = tyTop;
      hit = { point: { x: xTop, y: minY }, kind: "rail" };
    }
    const tyBottom = (maxY - cueBall.y) / dir.y;
    const xBottom = cueBall.x + tyBottom * dir.x;
    if (tyBottom > 0.0001 && xBottom >= minX && xBottom <= maxX && tyBottom < bestT) {
      bestT = tyBottom;
      hit = { point: { x: xBottom, y: maxY }, kind: "rail" };
    }
  }

  for (const pocket of POCKETS) {
    const t = rayCircleHit({ x: cueBall.x, y: cueBall.y }, dir, pocket, POCKET_RADIUS - 3);
    if (t !== null && t < bestT) {
      bestT = t;
      hit = {
        point: { x: cueBall.x + dir.x * t, y: cueBall.y + dir.y * t },
        kind: "pocket"
      };
    }
  }

  for (const ball of balls) {
    if (ball.isCue || ball.pocketed || ball.sinking) continue;
    const t = rayCircleHit({ x: cueBall.x, y: cueBall.y }, dir, { x: ball.x, y: ball.y }, BALL_RADIUS * 2);
    if (t !== null && t < bestT) {
      bestT = t;
      hit = {
        point: { x: cueBall.x + dir.x * t, y: cueBall.y + dir.y * t },
        kind: "ball",
        targetBall: ball
      };
    }
  }

  return hit;
}

function createCommandId(prefix: string): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
}

function buildRack(): Ball[] {
  const balls: Ball[] = [];
  const cueX = WORLD_WIDTH * 0.24;
  const cueY = WORLD_HEIGHT * 0.5;

  balls.push({
    id: 0,
    number: 0,
    x: cueX,
    y: cueY,
    vx: 0,
    vy: 0,
    color: "#ffffff",
    striped: false,
    isCue: true,
    pocketed: false,
    spinX: 0,
    spinY: 0,
    sinking: false,
    sinkProgress: 0,
    sinkPocket: null
  });

  const rackOriginX = WORLD_WIDTH * 0.72;
  const rackOriginY = WORLD_HEIGHT * 0.5;
  const rowSpacing = BALL_RADIUS * 1.9;
  const colSpacing = BALL_RADIUS * 1.72;

  const rackOrder = [1, 11, 4, 10, 8, 2, 15, 7, 12, 6, 13, 3, 14, 5, 9];

  let idx = 0;
  for (let row = 0; row < 5; row += 1) {
    for (let col = 0; col <= row; col += 1) {
      const number = rackOrder[idx];
      const isStripe = number >= 9;
      const paletteIndex = isStripe ? number - 9 : number - 1;
      const ballColor =
        number === 8
          ? "#111111"
          : isStripe
            ? STRIPE_COLORS[paletteIndex]
            : SOLID_COLORS[paletteIndex];

      balls.push({
        id: number,
        number,
        x: rackOriginX + row * colSpacing,
        y: rackOriginY - (row * rowSpacing) / 2 + col * rowSpacing,
        vx: 0,
        vy: 0,
        color: ballColor,
        striped: isStripe,
        isCue: false,
        pocketed: false,
        spinX: 0,
        spinY: 0,
        sinking: false,
        sinkProgress: 0,
        sinkPocket: null
      });
      idx += 1;
    }
  }

  return balls;
}

function drawBall(ctx: CanvasRenderingContext2D, ball: Ball, scale = 1, decalAlpha = 1): void {
  ctx.save();
  ctx.translate(ball.x, ball.y);
  ctx.scale(scale, scale);

  const gloss = ctx.createRadialGradient(-5, -6, 2, 0, 0, BALL_RADIUS + 6);
  gloss.addColorStop(0, "rgba(255,255,255,0.85)");
  gloss.addColorStop(0.35, ball.isCue ? "#f7f7f7" : ball.color);
  gloss.addColorStop(1, ball.isCue ? "#dbdbdb" : "#151515");

  ctx.fillStyle = gloss;
  ctx.beginPath();
  ctx.arc(0, 0, BALL_RADIUS, 0, Math.PI * 2);
  ctx.fill();

  if (ball.striped) {
    const stripeRotation = (ball.spinX + ball.spinY) * 0.45;
    ctx.rotate(stripeRotation);
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(0, 0, BALL_RADIUS * 0.88, 0.18 * Math.PI, 0.82 * Math.PI);
    ctx.arc(0, 0, BALL_RADIUS * 0.88, 1.18 * Math.PI, 1.82 * Math.PI);
    ctx.fill();

    ctx.fillStyle = ball.color;
    ctx.beginPath();
    ctx.ellipse(0, 0, BALL_RADIUS * 0.9, BALL_RADIUS * 0.5, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  if (!ball.isCue) {
    const markerX = Math.sin(ball.spinX) * BALL_RADIUS * 0.52;
    const markerY = Math.sin(ball.spinY) * BALL_RADIUS * 0.52;
    const markerVisibility = clamp((Math.cos(ball.spinX) + Math.cos(ball.spinY) + 2) / 4, 0.12, 1);
    const markerRadius = BALL_RADIUS * (0.18 + markerVisibility * 0.28);

    ctx.fillStyle = "rgba(255,255,255,0.95)";
    ctx.beginPath();
    ctx.arc(markerX, markerY, markerRadius, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = `rgba(17,17,17,${markerVisibility * decalAlpha})`;
    ctx.font = "bold 9px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(ball.number), markerX, markerY + 0.5);
  }

  ctx.restore();
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function PoolGame() {
  const { sessionID, roomID } = useParams<{ sessionID?: string; roomID?: string }>();
  const { firebaseUser, appUser } = useAuth();
  const dispatch = useAppDispatch();
  const roomState = useAppSelector(selectPoolRoomState);
  const poolError = useAppSelector(selectPoolError);
  const tournamentState = useAppSelector(selectPoolTournamentState);
  const poolSessionState = useAppSelector(selectPoolSessionState);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ballsRef = useRef<Ball[]>(buildRack());
  const pendingRackResetRef = useRef(false);
  const lastFrameRef = useRef<number>(0);
  const animationRef = useRef<number | null>(null);
  const appliedShotCommandRef = useRef<string | null>(null);
  const completedTurnForShotRef = useRef<string | null>(null);
  const pocketedThisTurnRef = useRef<number[]>([]);
  const cuePocketedThisTurnRef = useRef(false);
  const eightBallPocketThisTurnRef = useRef<number | null>(null);
  const pendingMatchWinnerRef = useRef<number | null>(null);
  const reportedMatchCommandRef = useRef<string | null>(null);
  const shotMotionObservedRef = useRef(false);
  const shotInFlightCommandRef = useRef<string | null>(null);
  const completeTurnInFlightRef = useRef(false);
  const appliedSnapshotVersionRef = useRef<number | null>(null);

  const [aimPoint, setAimPoint] = useState<Vec2 | null>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [power, setPower] = useState<number>(0);
  const [message, setMessage] = useState<string>("Break shot ready");
  const [selectedPocketIdx, setSelectedPocketIdx] = useState<number | null>(null);

  const activeBalls = ballsRef.current.filter((b) => !b.pocketed && !b.sinking);
  const cueBall = ballsRef.current.find((b) => b.isCue);
  const allStopped = activeBalls.every((ball) => Math.hypot(ball.vx, ball.vy) <= STOP_EPSILON);
  const anySinking = ballsRef.current.some((ball) => ball.sinking && !ball.pocketed);
  const isMultiplayer = Boolean(roomState);
  const isMyTurn = Boolean(
    roomState &&
      appUser &&
      roomState.status === "active" &&
      roomState.phase === "aiming" &&
      roomState.currentTurnUserId === appUser.id
  );
  const myPlayerName =
    roomState?.players.find((player) => player.userId === appUser?.id)?.username || appUser?.username || "You";
  const currentTurnPlayerName =
    roomState?.players.find((player) => player.userId === roomState.currentTurnUserId)?.username || "Opponent";
  const lastShotPlayerName =
    roomState?.players.find((player) => player.userId === roomState?.lastShot?.playerId)?.username || "Opponent";
  const myRack = roomState?.rackStates?.find((r) => r.userId === appUser?.id);
  const opponentRack = roomState?.rackStates?.find((r) => r.userId !== appUser?.id);
  const winnerName =
    roomState?.players.find((player) => player.userId === roomState?.winnerUserId)?.username || "Player";
  const activeTournamentMatch = tournamentState?.matches.find(
    (match) =>
      match.round === tournamentState.round &&
      (match.playerAUserId === appUser?.id || match.playerBUserId === appUser?.id) &&
      (match.status === "active" || match.status === "finished")
  );
  const tournamentInteractionLocked = Boolean(sessionID) && poolSessionState !== PoolSessionState.IN_MATCH;

  const canShoot = Boolean(
    cueBall &&
      !cueBall.pocketed &&
      !cueBall.sinking &&
      !tournamentInteractionLocked &&
      (isMultiplayer
        ? roomState?.status === "active" && roomState?.phase === "aiming" && isMyTurn
        : allStopped && !anySinking)
  );
  const opponentShotInProgress = Boolean(
    isMultiplayer &&
      roomState &&
      roomState.status === "active" &&
      roomState.phase === "balls_moving" &&
      roomState.lastShot &&
      roomState.lastShot.playerId !== appUser?.id
  );
  const myShotInProgress = Boolean(
    isMultiplayer &&
      roomState &&
      roomState.status === "active" &&
      roomState.phase === "balls_moving" &&
      roomState.lastShot &&
      roomState.lastShot.playerId === appUser?.id
  );
  const waitingForOpponent = Boolean(
    isMultiplayer &&
      roomState &&
      roomState.status === "active" &&
      !isMyTurn &&
      !myShotInProgress
  );
  const shotAssistMode: ShotAssistMode =
    tournamentState?.shotAssistMode === "off" || tournamentState?.shotAssistMode === "full"
      ? tournamentState.shotAssistMode
      : "basic";
  const myGroup = myRack?.group;
  const isAssignedGroup = myGroup === "solids" || myGroup === "stripes";
  const myGroupPocketed = (myRack?.pocketedBalls || []).length;
  const isOnEightBall = Boolean(isAssignedGroup && myGroupPocketed >= 7);

  const applyShot = useCallback((direction: Vec2, shotPower: number) => {
    if (!cueBall) {
      return;
    }

    const dir = normalize(direction);
    if (magnitude(dir) <= 0.001) {
      return;
    }

    pocketedThisTurnRef.current = [];
    cuePocketedThisTurnRef.current = false;
    eightBallPocketThisTurnRef.current = null;
    shotMotionObservedRef.current = false;

    const baseSpeed = 200 + (shotPower / 100) * 900;
    cueBall.vx = dir.x * clamp(baseSpeed, 0, MAX_SPEED);
    cueBall.vy = dir.y * clamp(baseSpeed, 0, MAX_SPEED);
    setMessage("Shot taken");
    setAimPoint(null);
    setPower(0);
    setDragState(null);
  }, [cueBall]);

  const toSnapshot = useCallback((): PoolBallState[] => {
    return ballsRef.current.map((ball) => ({
      number: ball.number,
      x: Math.round(ball.x * 1000) / 1000,
      y: Math.round(ball.y * 1000) / 1000,
      pocketed: ball.pocketed
    }));
  }, []);

  const applySnapshot = useCallback((snapshot: PoolBallState[]) => {
    if (!snapshot || snapshot.length === 0) {
      return;
    }

    const byNumber = new Map<number, PoolBallState>();
    snapshot.forEach((ball) => byNumber.set(ball.number, ball));

    ballsRef.current = ballsRef.current.map((ball) => {
      const synced = byNumber.get(ball.number);
      if (!synced) {
        return ball;
      }

      return {
        ...ball,
        x: synced.x,
        y: synced.y,
        pocketed: synced.pocketed,
        vx: 0,
        vy: 0,
        sinking: false,
        sinkProgress: 0,
        sinkPocket: null
      };
    });
  }, []);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    ctx.clearRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

    const feltGradient = ctx.createLinearGradient(0, 0, 0, WORLD_HEIGHT);
    feltGradient.addColorStop(0, "#2fbf9a");
    feltGradient.addColorStop(1, "#1e7f73");

    const cushionInset = CUSHION_INSET;
    const feltInset = FELT_INSET;

    const getPocketVisual = (idx: number) => {
      const isMiddlePocket = idx === 1 || idx === 4;
      const angle = isMiddlePocket ? Math.PI / 2 : idx === 0 || idx === 5 ? Math.PI / 4 : -Math.PI / 4;
      const capW = isMiddlePocket ? 112 : 86;
      const capH = isMiddlePocket ? 40 : 86;

      let capCenterX = WORLD_WIDTH / 2;
      let capCenterY = WORLD_HEIGHT / 2;
      if (idx === 0) {
        capCenterX = capW / 2;
        capCenterY = capH / 2;
      } else if (idx === 2) {
        capCenterX = WORLD_WIDTH - capW / 2;
        capCenterY = capH / 2;
      } else if (idx === 3) {
        capCenterX = capW / 2;
        capCenterY = WORLD_HEIGHT - capH / 2;
      } else if (idx === 5) {
        capCenterX = WORLD_WIDTH - capW / 2;
        capCenterY = WORLD_HEIGHT - capH / 2;
      } else if (idx === 1) {
        capCenterX = WORLD_WIDTH / 2;
        capCenterY = capH / 2;
      } else if (idx === 4) {
        capCenterX = WORLD_WIDTH / 2;
        capCenterY = WORLD_HEIGHT - capH / 2;
      }

      const linerW = isMiddlePocket ? 40 : 40;
      const linerH = isMiddlePocket ? 36 : 36;
      const voidW = linerW - 10;
      const voidH = linerH - 8;
      return { angle, capW, capH, capCenterX, capCenterY, linerW, linerH, voidW, voidH, isMiddlePocket };
    };

    // Wood layer above base felt.
    ctx.fillStyle = "#4f2b18";
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

    // Collar base above wood, below top felt.
    POCKETS.forEach((_, idx) => {
      const visual = getPocketVisual(idx);
      ctx.save();
      ctx.translate(visual.capCenterX, visual.capCenterY);
      ctx.fillStyle = "#d8bf9b";
      roundedRectPath(ctx, -visual.capW / 2, -visual.capH / 2, visual.capW, visual.capH, 11);
      ctx.fill();
      ctx.strokeStyle = "rgba(93, 69, 44, 0.65)";
      ctx.lineWidth = 1.8;
      ctx.stroke();
      ctx.restore();
    });

    const cushionGradient = ctx.createLinearGradient(0, cushionInset, 0, WORLD_HEIGHT - cushionInset);
    cushionGradient.addColorStop(0, "#2aa88c");
    cushionGradient.addColorStop(1, "#1b6d63");
    const drawRailPad = (points: Vec2[]) => {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i += 1) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    };

    const drawAllRailPads = () => {
      ctx.fillStyle = cushionGradient;
      ctx.strokeStyle = "rgba(18, 63, 58, 0.45)";
      ctx.lineWidth = 1.1;

      PAD_POLYGONS.forEach((polygon) => drawRailPad(polygon));
    };

    ctx.fillStyle = feltGradient;
    ctx.fillRect(feltInset, feltInset, WORLD_WIDTH - feltInset * 2, WORLD_HEIGHT - feltInset * 2);
    drawAllRailPads();

    // Decorative rail dots (sights), styled like pocket collars.
    const sightColor = "#d2b48c";
    const dotRadius = 3.6;
    const topBottomInset = 112;
    const sideInset = 118;
    const topBottomCount = 6;
    const sideCount = 3;
    const topBottomStep = (WORLD_WIDTH - topBottomInset * 2) / (topBottomCount - 1);
    const sideStep = (WORLD_HEIGHT - sideInset * 2) / (sideCount - 1);

    ctx.fillStyle = sightColor;
    for (let i = 0; i < topBottomCount; i += 1) {
      const x = topBottomInset + i * topBottomStep;
      const topY = cushionInset * 0.5;
      const bottomY = WORLD_HEIGHT - cushionInset * 0.5;
      ctx.beginPath();
      ctx.arc(x, topY, dotRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, bottomY, dotRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    for (let i = 0; i < sideCount; i += 1) {
      const y = sideInset + i * sideStep;
      const leftX = cushionInset * 0.5;
      const rightX = WORLD_WIDTH - cushionInset * 0.5;
      ctx.beginPath();
      ctx.arc(leftX, y, dotRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(rightX, y, dotRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    // Pocket mouths under balls.
    POCKETS.forEach((pocket, idx) => {
      const visual = getPocketVisual(idx);
      ctx.save();
      // Push mouth slightly toward table edge so it sits less on felt.
      const edgeDir = normalize({ x: pocket.x - WORLD_WIDTH / 2, y: pocket.y - WORLD_HEIGHT / 2 });
      const mouthOffset = visual.isMiddlePocket ? 10 : 8;
      ctx.translate(pocket.x + edgeDir.x * mouthOffset, pocket.y + edgeDir.y * mouthOffset);
      ctx.rotate(visual.angle);

      const voidGradient = ctx.createRadialGradient(0, -2, 2, 0, 0, visual.voidW * 0.55);
      voidGradient.addColorStop(0, "#050505");
      voidGradient.addColorStop(1, "#141414");
      ctx.fillStyle = voidGradient;
      roundedRectPath(ctx, -visual.linerW / 2, -visual.linerH / 2, visual.linerW, visual.linerH, visual.linerH * 0.46);
      ctx.fill();

      ctx.fillStyle = "#090909";
      roundedRectPath(ctx, -visual.voidW / 2, -visual.voidH / 2, visual.voidW, visual.voidH, visual.voidH * 0.46);
      ctx.fill();
      ctx.restore();

      if (isOnEightBall && canShoot) {
        const isSelected = selectedPocketIdx === idx;
        ctx.save();
        ctx.strokeStyle = isSelected ? "rgba(255, 215, 80, 0.98)" : "rgba(255,255,255,0.55)";
        ctx.lineWidth = isSelected ? 3.4 : 2;
        ctx.beginPath();
        ctx.arc(pocket.x, pocket.y, POCKET_RADIUS + (isSelected ? 14 : 11), 0, Math.PI * 2);
        ctx.stroke();
        if (isSelected) {
          ctx.fillStyle = "rgba(255, 215, 80, 0.24)";
          ctx.beginPath();
          ctx.arc(pocket.x, pocket.y, POCKET_RADIUS + 9, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
    });

    ballsRef.current.forEach((ball) => {
      if (ball.pocketed) {
        return;
      }

      const sinkProgress = ball.sinkProgress || 0;
      const renderScale = 1 - sinkProgress * 0.55;
      const sinkYOffset = sinkProgress * 18;
      const shadowOpacity = 0.18 * (1 - sinkProgress);

      ctx.fillStyle = `rgba(0,0,0,${shadowOpacity})`;
      ctx.beginPath();
      ctx.ellipse(
        ball.x,
        ball.y + BALL_RADIUS * 0.92 + sinkYOffset * 0.35,
        BALL_RADIUS * 0.74 * renderScale,
        BALL_RADIUS * 0.42 * renderScale,
        0,
        0,
        Math.PI * 2
      );
      ctx.fill();

      const renderBall = { ...ball, y: ball.y + sinkYOffset };
      drawBall(ctx, renderBall, renderScale, 1 - sinkProgress * 0.65);
    });

    if (canShoot && cueBall && aimPoint) {
      const direction = dragState?.direction ?? normalize({ x: aimPoint.x - cueBall.x, y: aimPoint.y - cueBall.y });
      if (magnitude(direction) <= 0.0001) {
        return;
      }
      if (shotAssistMode !== "off") {
        const startX = cueBall.x + direction.x * (BALL_RADIUS + 8);
        const startY = cueBall.y + direction.y * (BALL_RADIUS + 8);
        const assistHit = shotAssistMode === "full" ? computeAssistHit(cueBall, direction, ballsRef.current) : null;
        const guideLength = shotAssistMode === "full" ? 540 : 320;
        const endX = assistHit ? assistHit.point.x : cueBall.x + direction.x * guideLength;
        const endY = assistHit ? assistHit.point.y : cueBall.y + direction.y * guideLength;

        ctx.strokeStyle = "rgba(255,255,255,0.82)";
        ctx.setLineDash([10, 7]);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
        ctx.setLineDash([]);

        if (shotAssistMode === "full" && assistHit) {
          if (assistHit.kind === "ball" && assistHit.targetBall) {
            const playerGroup = myRack?.group || "open";
            const targetGroup = ballGroup(assistHit.targetBall.number);
            const myGroupPocketed = (myRack?.pocketedBalls || []).length;
            const myGroupRemaining =
              playerGroup === "solids" || playerGroup === "stripes" ? Math.max(0, 7 - myGroupPocketed) : 0;
            const earlyEightBall = assistHit.targetBall.number === 8 && myGroupRemaining > 0;
            const wrongTarget =
              earlyEightBall ||
              (playerGroup === "solids" || playerGroup === "stripes") &&
              (targetGroup === "solids" || targetGroup === "stripes") &&
              targetGroup !== playerGroup;

            // Impact marker: show the exact projected contact point, not the whole target ball.
            ctx.strokeStyle = wrongTarget ? "rgba(255, 94, 94, 0.95)" : "rgba(255, 240, 220, 0.9)";
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(assistHit.point.x, assistHit.point.y, 5.5, 0, Math.PI * 2);
            ctx.stroke();

            if (wrongTarget) {
              ctx.strokeStyle = "rgba(255, 70, 70, 0.96)";
              ctx.lineWidth = 2.2;
              const mark = 5;
              ctx.beginPath();
              ctx.moveTo(assistHit.point.x - mark, assistHit.point.y - mark);
              ctx.lineTo(assistHit.point.x + mark, assistHit.point.y + mark);
              ctx.moveTo(assistHit.point.x + mark, assistHit.point.y - mark);
              ctx.lineTo(assistHit.point.x - mark, assistHit.point.y + mark);
              ctx.stroke();
            }

            if (!wrongTarget) {
              // Target-ball path should follow the impact normal (line of centers at contact),
              // not the incoming cue direction.
              const impactNormal = normalize({
                x: assistHit.targetBall.x - assistHit.point.x,
                y: assistHit.targetBall.y - assistHit.point.y
              });
              const carryEndX = assistHit.targetBall.x + impactNormal.x * 85;
              const carryEndY = assistHit.targetBall.y + impactNormal.y * 85;
              ctx.strokeStyle = "rgba(255, 218, 140, 0.9)";
              ctx.setLineDash([7, 5]);
              ctx.beginPath();
              ctx.moveTo(assistHit.targetBall.x, assistHit.targetBall.y);
              ctx.lineTo(carryEndX, carryEndY);
              ctx.stroke();
              ctx.setLineDash([]);
            }
          } else {
            ctx.fillStyle = "rgba(255,255,255,0.82)";
            ctx.beginPath();
            ctx.arc(endX, endY, 4, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      const cueOffset = 48 + (power / 100) * 62;
      const cueLength = 290;
      const backX = cueBall.x - direction.x * cueOffset;
      const backY = cueBall.y - direction.y * cueOffset;
      const tailX = backX - direction.x * cueLength;
      const tailY = backY - direction.y * cueLength;

      const tipX = backX + direction.x * 8;
      const tipY = backY + direction.y * 8;
      const ferruleX = backX - direction.x * 10;
      const ferruleY = backY - direction.y * 10;
      const wrapX = tailX + direction.x * 78;
      const wrapY = tailY + direction.y * 78;
      const buttX = tailX + direction.x * 18;
      const buttY = tailY + direction.y * 18;

      // soft cyan accent bloom to make cue feel premium
      ctx.strokeStyle = "rgba(97, 219, 255, 0.18)";
      ctx.lineWidth = 12;
      ctx.beginPath();
      ctx.moveTo(backX, backY);
      ctx.lineTo(tailX, tailY);
      ctx.stroke();

      // tip
      ctx.strokeStyle = "#2d8bb8";
      ctx.lineWidth = 4.2;
      ctx.beginPath();
      ctx.moveTo(tipX, tipY);
      ctx.lineTo(backX, backY);
      ctx.stroke();

      // ferrule
      ctx.strokeStyle = "#f1efe8";
      ctx.lineWidth = 6.2;
      ctx.beginPath();
      ctx.moveTo(backX, backY);
      ctx.lineTo(ferruleX, ferruleY);
      ctx.stroke();

      // shaft
      ctx.lineWidth = 8.5;
      const cueGradient = ctx.createLinearGradient(backX, backY, tailX, tailY);
      cueGradient.addColorStop(0, "#f6dfbe");
      cueGradient.addColorStop(0.5, "#cb8a49");
      cueGradient.addColorStop(1, "#6a3f1f");
      ctx.strokeStyle = cueGradient;
      ctx.beginPath();
      ctx.moveTo(ferruleX, ferruleY);
      ctx.lineTo(tailX, tailY);
      ctx.stroke();

      // grip wrap
      ctx.strokeStyle = "#213d53";
      ctx.lineWidth = 9.4;
      ctx.beginPath();
      ctx.moveTo(wrapX, wrapY);
      ctx.lineTo(tailX, tailY);
      ctx.stroke();

      // butt cap
      ctx.strokeStyle = "#d2b48c";
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.moveTo(buttX, buttY);
      ctx.lineTo(tailX, tailY);
      ctx.stroke();
    }

  }, [aimPoint, canShoot, cueBall, dragState, isOnEightBall, myRack?.group, power, selectedPocketIdx, shotAssistMode]);

  const stepSimulation = useCallback((dt: number) => {
    const balls = ballsRef.current;
    const active = balls.filter((ball) => !ball.pocketed && !ball.sinking);
    const sinking = balls.filter((ball) => !ball.pocketed && ball.sinking);
    if (active.some((ball) => Math.hypot(ball.vx, ball.vy) > STOP_EPSILON) || sinking.length > 0) {
      shotMotionObservedRef.current = true;
    }

    sinking.forEach((ball) => {
      ball.vx = 0;
      ball.vy = 0;
      ball.sinkProgress = clamp(ball.sinkProgress + dt * 2.8, 0, 1);

      if (ball.sinkPocket) {
        ball.x += (ball.sinkPocket.x - ball.x) * clamp(dt * 14, 0, 1);
        ball.y += (ball.sinkPocket.y - ball.y) * clamp(dt * 14, 0, 1);
      }

      if (ball.sinkProgress >= 1) {
        if (ball.isCue) {
          ball.pocketed = false;
          ball.sinking = false;
          ball.sinkProgress = 0;
          ball.sinkPocket = null;
          ball.x = WORLD_WIDTH * 0.24;
          ball.y = WORLD_HEIGHT * 0.5;
          ball.spinX = 0;
          ball.spinY = 0;
        } else {
          ball.pocketed = true;
        }

        if (ball.number === 8 && pendingRackResetRef.current) {
          ballsRef.current = buildRack();
          pendingRackResetRef.current = false;
          setAimPoint(null);
          setPower(0);
          setDragState(null);
        }
      }
    });

    const friction = 0.988;

    active.forEach((ball) => {
      ball.x += ball.vx * dt;
      ball.y += ball.vy * dt;
      ball.spinX += (ball.vx * dt) / BALL_RADIUS;
      ball.spinY += (ball.vy * dt) / BALL_RADIUS;

      ball.vx *= friction;
      ball.vy *= friction;

      if (Math.hypot(ball.vx, ball.vy) < STOP_EPSILON) {
        ball.vx = 0;
        ball.vy = 0;
      }

      // Collide against actual rendered pad geometry (segment colliders), not an invisible rail box.
      for (let iter = 0; iter < 2; iter += 1) {
        for (const seg of PAD_SEGMENTS) {
          const closest = closestPointOnSegment({ x: ball.x, y: ball.y }, seg.a, seg.b);
          const dx = ball.x - closest.x;
          const dy = ball.y - closest.y;
          const distSq = dx * dx + dy * dy;
          if (distSq >= BALL_RADIUS * BALL_RADIUS) {
            continue;
          }

          let nx = 0;
          let ny = 0;
          const dist = Math.sqrt(distSq);
          if (dist > 0.0001) {
            nx = dx / dist;
            ny = dy / dist;
          } else {
            const sx = seg.b.x - seg.a.x;
            const sy = seg.b.y - seg.a.y;
            const sl = Math.hypot(sx, sy) || 1;
            nx = -sy / sl;
            ny = sx / sl;
          }

          const penetration = BALL_RADIUS - dist;
          ball.x += nx * (penetration + 0.02);
          ball.y += ny * (penetration + 0.02);

          const vn = ball.vx * nx + ball.vy * ny;
          if (vn < 0) {
            ball.vx -= (1 + RAIL_RESTITUTION) * vn * nx;
            ball.vy -= (1 + RAIL_RESTITUTION) * vn * ny;
          }
        }
      }

      // Failsafe: keep balls inside world bounds.
      if (ball.x < BALL_RADIUS) {
        ball.x = BALL_RADIUS;
        ball.vx = Math.abs(ball.vx) * RAIL_RESTITUTION;
      } else if (ball.x > WORLD_WIDTH - BALL_RADIUS) {
        ball.x = WORLD_WIDTH - BALL_RADIUS;
        ball.vx = -Math.abs(ball.vx) * RAIL_RESTITUTION;
      }
      if (ball.y < BALL_RADIUS) {
        ball.y = BALL_RADIUS;
        ball.vy = Math.abs(ball.vy) * RAIL_RESTITUTION;
      } else if (ball.y > WORLD_HEIGHT - BALL_RADIUS) {
        ball.y = WORLD_HEIGHT - BALL_RADIUS;
        ball.vy = -Math.abs(ball.vy) * RAIL_RESTITUTION;
      }

      for (let pocketIdx = 0; pocketIdx < POCKETS.length; pocketIdx += 1) {
        const pocket = POCKETS[pocketIdx];
        const d = Math.hypot(ball.x - pocket.x, ball.y - pocket.y);
        // Start sink only once the ball is fully inside the pocket radius.
        if (d <= POCKET_RADIUS - BALL_RADIUS) {
          ball.sinking = true;
          ball.sinkProgress = 0;
          ball.sinkPocket = pocket;
          ball.vx *= 0.18;
          ball.vy *= 0.18;

          if (ball.isCue) {
            cuePocketedThisTurnRef.current = true;
            setMessage("Scratch. Cue ball reset.");
          } else if (ball.number === 8) {
            eightBallPocketThisTurnRef.current = pocketIdx;
            if (isMultiplayer) {
              if (!pocketedThisTurnRef.current.includes(8)) {
                pocketedThisTurnRef.current.push(8);
              }
              setMessage("8-ball pocketed. Validating...");
            } else {
              setMessage("8-ball pocketed. Rack reset.");
              pendingRackResetRef.current = true;
            }
          } else {
            if (!pocketedThisTurnRef.current.includes(ball.number)) {
              pocketedThisTurnRef.current.push(ball.number);
            }
            setMessage(`Pocketed ${ball.number}.`);
          }
          break;
        }
      }
    });

    for (let i = 0; i < active.length; i += 1) {
      for (let j = i + 1; j < active.length; j += 1) {
        const a = active[i];
        const b = active[j];

        if (a.pocketed || b.pocketed) {
          continue;
        }

        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.hypot(dx, dy);
        const minDist = BALL_RADIUS * 2;

        if (dist <= 0 || dist >= minDist) {
          continue;
        }

        const nx = dx / dist;
        const ny = dy / dist;
        const overlap = minDist - dist;

        a.x -= nx * (overlap / 2);
        a.y -= ny * (overlap / 2);
        b.x += nx * (overlap / 2);
        b.y += ny * (overlap / 2);

        const rvx = b.vx - a.vx;
        const rvy = b.vy - a.vy;
        const speedAlongNormal = rvx * nx + rvy * ny;

        if (speedAlongNormal > 0) {
          continue;
        }

        const impulse = (-(1 + BALL_RESTITUTION) * speedAlongNormal) / 2;

        const impulseX = impulse * nx;
        const impulseY = impulse * ny;

        a.vx -= impulseX;
        a.vy -= impulseY;
        b.vx += impulseX;
        b.vy += impulseY;
      }
    }
  }, [isMultiplayer, roomState?.lastShot?.playerId, roomState?.matchId]);

  const gameLoop = useCallback(
    (now: number) => {
      const previous = lastFrameRef.current || now;
      lastFrameRef.current = now;
      const frameDelta = clamp((now - previous) / 1000, 0, 0.03);

      stepSimulation(frameDelta);
      render();

      animationRef.current = requestAnimationFrame(gameLoop);
    },
    [render, stepSimulation]
  );

  useEffect(() => {
    render();
    animationRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameLoop, render]);

  useEffect(() => {
    if (!firebaseUser || !roomID) {
      return;
    }
    if (roomState?.roomId === roomID) {
      return;
    }

    (async () => {
      try {
        const idToken = await firebaseUser.getIdToken();
        const state = await fetchPoolRoomState(roomID, idToken);
        dispatch(poolStateHydrated(state));
        setMessage(`Joined room ${state.roomId}.`);
      } catch (error) {
        console.error("Failed to load pool room by route", error);
      }
    })();
  }, [dispatch, firebaseUser, roomID, roomState?.roomId]);

  useEffect(() => {
    if (roomID) {
      return;
    }
    if (!firebaseUser || !activeTournamentMatch?.roomId) {
      return;
    }
    if (roomState?.roomId === activeTournamentMatch.roomId) {
      return;
    }

    (async () => {
      try {
        const idToken = await firebaseUser.getIdToken();
        const state = await fetchPoolRoomState(activeTournamentMatch.roomId as string, idToken);
        dispatch(poolStateHydrated(state));
        setMessage(`Auto-joined tournament match room ${state.roomId}.`);
      } catch (error) {
        console.error("Failed to auto-load tournament match room", error);
      }
    })();
  }, [activeTournamentMatch?.roomId, dispatch, firebaseUser, roomID, roomState?.roomId]);

  useEffect(() => {
    const incomingShot = roomState?.lastShot;
    if (!incomingShot || roomState?.phase !== "balls_moving") {
      return;
    }

    if (appliedShotCommandRef.current === incomingShot.commandId) {
      return;
    }

    if (roomState.lastSnapshot && roomState.lastSnapshot.length > 0) {
      applySnapshot(roomState.lastSnapshot);
      appliedSnapshotVersionRef.current = roomState.version;
    }

    appliedShotCommandRef.current = incomingShot.commandId;
    completedTurnForShotRef.current = null;
    if (incomingShot.playerId === appUser?.id) {
      shotInFlightCommandRef.current = incomingShot.commandId;
      applyShot({ x: incomingShot.directionX, y: incomingShot.directionY }, incomingShot.power);
    } else {
      shotInFlightCommandRef.current = null;
      shotMotionObservedRef.current = false;
      completeTurnInFlightRef.current = false;
      setMessage(`${lastShotPlayerName} has shot...`);
    }
  }, [appUser?.id, applyShot, applySnapshot, lastShotPlayerName, roomState]);

  useEffect(() => {
    if (!roomState?.roomId) {
      return;
    }
    ballsRef.current = buildRack();
    pendingRackResetRef.current = false;
    pendingMatchWinnerRef.current = null;
    reportedMatchCommandRef.current = null;
    appliedShotCommandRef.current = null;
    completedTurnForShotRef.current = null;
    shotInFlightCommandRef.current = null;
    eightBallPocketThisTurnRef.current = null;
    shotMotionObservedRef.current = false;
    completeTurnInFlightRef.current = false;
    appliedSnapshotVersionRef.current = null;
    setAimPoint(null);
    setPower(0);
    setDragState(null);
    setSelectedPocketIdx(null);

    if (roomState.lastSnapshot && roomState.lastSnapshot.length > 0) {
      applySnapshot(roomState.lastSnapshot);
      appliedSnapshotVersionRef.current = roomState.version;
    }
  }, [applySnapshot, roomState?.lastSnapshot, roomState?.roomId, roomState?.version]);

  useEffect(() => {
    if (!roomState?.lastSnapshot || roomState.lastSnapshot.length === 0) {
      return;
    }
    if (roomState.lastEvent !== "turn_completed") {
      return;
    }
    if (appliedSnapshotVersionRef.current === roomState.version) {
      return;
    }

    applySnapshot(roomState.lastSnapshot);
    appliedSnapshotVersionRef.current = roomState.version;
    shotInFlightCommandRef.current = null;
    shotMotionObservedRef.current = false;
    completeTurnInFlightRef.current = false;
  }, [applySnapshot, roomState]);

  useEffect(() => {
    if (!isMyTurn) {
      setAimPoint(null);
      setDragState(null);
      setPower(0);
      setSelectedPocketIdx(null);
    }
  }, [isMyTurn]);

  useEffect(() => {
    if (!isMultiplayer || !roomState || !appUser || !firebaseUser) {
      return;
    }
    if (pendingMatchWinnerRef.current) {
      return;
    }
    if (roomState.phase !== "balls_moving") {
      return;
    }
    if (!roomState.lastShot || roomState.lastShot.playerId !== appUser.id) {
      return;
    }
    if (completedTurnForShotRef.current === roomState.lastShot.commandId) {
      return;
    }
    if (completeTurnInFlightRef.current) {
      return;
    }

    const interval = setInterval(async () => {
      const shotCommandID = roomState.lastShot?.commandId;
      if (!shotCommandID) {
        return;
      }
      const balls = ballsRef.current;
      const moving = balls.some((ball) => !ball.pocketed && !ball.sinking && Math.hypot(ball.vx, ball.vy) > STOP_EPSILON);
      const sinking = balls.some((ball) => !ball.pocketed && ball.sinking);
      const settled = !moving && !sinking;

      if (!shotMotionObservedRef.current || !settled) {
        return;
      }
      if (completedTurnForShotRef.current === shotCommandID || completeTurnInFlightRef.current) {
        return;
      }

      completeTurnInFlightRef.current = true;
      completedTurnForShotRef.current = shotCommandID;

      const keepTurn = pocketedThisTurnRef.current.length > 0 && !cuePocketedThisTurnRef.current;
      const foul = cuePocketedThisTurnRef.current;

      try {
        const idToken = await firebaseUser.getIdToken();
        const updated = await completePoolTurn(
          {
            roomId: roomState.roomId,
            commandId: createCommandId("turn"),
            keepTurn,
            foul,
            cuePocketed: cuePocketedThisTurnRef.current,
            pocketedBalls: pocketedThisTurnRef.current,
            eightBallPocket: eightBallPocketThisTurnRef.current ?? undefined,
            balls: toSnapshot()
          },
          idToken
        );
        dispatch(poolStateHydrated(updated));
      } catch (error) {
        console.error("Failed to complete pool turn", error);
        completedTurnForShotRef.current = null;
        completeTurnInFlightRef.current = false;
      }
    }, 80);

    return () => clearInterval(interval);
  }, [appUser, dispatch, firebaseUser, isMultiplayer, roomState, toSnapshot]);

  useEffect(() => {
    if (!firebaseUser || !roomState || roomState.status !== "active") {
      return;
    }
    if (!roomState.matchId || !roomState.tournamentId) {
      return;
    }
    if (anySinking || !allStopped) {
      return;
    }

    const winnerUserId = pendingMatchWinnerRef.current;
    if (!winnerUserId) {
      return;
    }

    const completionKey = `${roomState.matchId}-${winnerUserId}`;
    if (reportedMatchCommandRef.current === completionKey) {
      return;
    }
    reportedMatchCommandRef.current = completionKey;

    (async () => {
      try {
        const idToken = await firebaseUser.getIdToken();
        const updatedRoom = await completePoolMatch(
          {
            roomId: roomState.roomId,
            commandId: createCommandId("complete-match"),
            winnerUserId
          },
          idToken
        );
        pendingMatchWinnerRef.current = null;
        dispatch(poolStateHydrated(updatedRoom));
        setMessage(`Match finished. Winner: ${updatedRoom.players.find((p) => p.userId === updatedRoom.winnerUserId)?.username || "Player"}`);
      } catch (error) {
        console.error("Failed to auto-complete match", error);
      }
    })();
  }, [allStopped, anySinking, dispatch, firebaseUser, roomState]);

  useEffect(() => {
    if (!firebaseUser || !roomState) {
      return;
    }
    if (roomState.lastEvent !== "turn_forfeit" || !roomState.winnerUserId) {
      return;
    }
    if (!roomState.matchId || !roomState.tournamentId) {
      return;
    }

    const completionKey = `${roomState.matchId}-${roomState.winnerUserId}-forfeit`;
    if (reportedMatchCommandRef.current === completionKey) {
      return;
    }
    reportedMatchCommandRef.current = completionKey;

    (async () => {
      try {
        const idToken = await firebaseUser.getIdToken();
        const updatedRoom = await completePoolMatch(
          {
            roomId: roomState.roomId,
            commandId: createCommandId("complete-match-forfeit"),
            winnerUserId: roomState.winnerUserId as number
          },
          idToken
        );
        dispatch(poolStateHydrated(updatedRoom));
        setMessage(
          `Match forfeited. Winner: ${updatedRoom.players.find((p) => p.userId === updatedRoom.winnerUserId)?.username || "Player"}`
        );
      } catch (error) {
        console.error("Failed to auto-complete forfeited match", error);
      }
    })();
  }, [dispatch, firebaseUser, roomState]);

  useEffect(() => {
    if (!firebaseUser || !roomState) {
      return;
    }
    if (roomState.lastEvent !== "eight_ball_resolved" || !roomState.winnerUserId) {
      return;
    }
    if (!roomState.matchId || !roomState.tournamentId) {
      return;
    }

    const completionKey = `${roomState.matchId}-${roomState.winnerUserId}-eight`;
    if (reportedMatchCommandRef.current === completionKey) {
      return;
    }
    reportedMatchCommandRef.current = completionKey;

    (async () => {
      try {
        const idToken = await firebaseUser.getIdToken();
        const updatedRoom = await completePoolMatch(
          {
            roomId: roomState.roomId,
            commandId: createCommandId("complete-match-eight"),
            winnerUserId: roomState.winnerUserId as number
          },
          idToken
        );
        dispatch(poolStateHydrated(updatedRoom));
        setMessage(
          `Match finished. Winner: ${updatedRoom.players.find((p) => p.userId === updatedRoom.winnerUserId)?.username || "Player"}`
        );
      } catch (error) {
        console.error("Failed to auto-complete 8-ball resolved match", error);
      }
    })();
  }, [dispatch, firebaseUser, roomState]);

  const getCanvasPoint = useCallback((event: React.PointerEvent<HTMLCanvasElement>): Vec2 | null => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return null;
    }

    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return null;
    }

    const scaleX = WORLD_WIDTH / rect.width;
    const scaleY = WORLD_HEIGHT / rect.height;

    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY
    };
  }, []);

  const handleAimMove = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (!canShoot || !cueBall) {
        return;
      }

      const p = getCanvasPoint(event);
      if (!p) {
        return;
      }

      if (!dragState || dragState.pointerId !== event.pointerId) {
        setAimPoint(p);
        return;
      }

      const dragVector = { x: p.x - dragState.start.x, y: p.y - dragState.start.y };
      const backDirection = { x: -dragState.direction.x, y: -dragState.direction.y };
      const dragBackAmount = dragVector.x * backDirection.x + dragVector.y * backDirection.y;
      const nextPower = clamp((dragBackAmount / 190) * 100, 0, 100);
      setPower(nextPower);
    },
    [canShoot, cueBall, dragState, getCanvasPoint]
  );

  const handleAimStart = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (!canShoot || !cueBall) {
        return;
      }

      const p = getCanvasPoint(event);
      if (!p) {
        return;
      }

      const direction = normalize({ x: p.x - cueBall.x, y: p.y - cueBall.y });
      if (magnitude(direction) <= 0.0001) {
        return;
      }

      if (isOnEightBall) {
        let selected: number | null = null;
        for (let i = 0; i < POCKETS.length; i += 1) {
          const pocket = POCKETS[i];
          if (Math.hypot(p.x - pocket.x, p.y - pocket.y) <= POCKET_RADIUS + 16) {
            selected = i;
            break;
          }
        }
        if (selected !== null) {
          setSelectedPocketIdx(selected);
          setMessage(`Called pocket #${selected + 1}`);
          return;
        }
      }

      event.currentTarget.setPointerCapture(event.pointerId);
      setAimPoint(p);
      setPower(0);
      setDragState({
        pointerId: event.pointerId,
        start: p,
        direction
      });
    },
    [canShoot, cueBall, getCanvasPoint, isOnEightBall]
  );

  const handleAimRelease = useCallback(
    async (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }

      event.currentTarget.releasePointerCapture(event.pointerId);

      if (power < MIN_SHOT_POWER) {
        setPower(0);
        setDragState(null);
        setMessage("Aim locked. Pull back more to shoot.");
        return;
      }
      if (isOnEightBall && selectedPocketIdx === null) {
        setMessage("Call a pocket for the 8-ball before shooting.");
        setPower(0);
        setDragState(null);
        return;
      }

      if (isMultiplayer && roomState?.roomId) {
        if (!firebaseUser) {
          setMessage("Sign in required for multiplayer.");
          return;
        }
        try {
          const idToken = await firebaseUser.getIdToken();
          const updated = await submitPoolShot(
            {
              roomId: roomState.roomId,
              commandId: createCommandId("shot"),
              directionX: dragState.direction.x,
              directionY: dragState.direction.y,
              power,
              calledPocket: isOnEightBall ? selectedPocketIdx ?? undefined : undefined
            },
            idToken
          );
          dispatch(poolStateHydrated(updated));
          if (isOnEightBall) {
            setSelectedPocketIdx(null);
          }
        } catch (error) {
          setMessage(error instanceof Error ? error.message : "Shot rejected");
        }
        return;
      }

      applyShot(dragState.direction, power);
    },
    [applyShot, dispatch, dragState, firebaseUser, isMultiplayer, isOnEightBall, power, roomState?.roomId, selectedPocketIdx]
  );

  return (
    <div className="pool-page">
      <div className="pool-status-row">
        <span>{message}</span>
        <span>
          {tournamentInteractionLocked
            ? poolSessionState === PoolSessionState.WAITING_FOR_START || poolSessionState === PoolSessionState.LOBBY
              ? "Lobby locked until tournament starts"
              : "Waiting for your next match"
            : isMultiplayer
            ? isMyTurn
              ? `${myPlayerName}: hold, pull back, release`
              : myShotInProgress
                ? "Shot in progress..."
              : opponentShotInProgress
                ? `${lastShotPlayerName} has shot...`
                : `Waiting for ${currentTurnPlayerName}...`
            : canShoot
              ? "Hold to lock aim, pull back, release to shoot"
              : "Balls in motion"}
          {poolError ? ` • ${poolError}` : ""}
        </span>
      </div>

      {roomState?.status === "finished" && roomState?.winnerUserId && (
        <div className="pool-endgame-overlay">
          <div className="pool-endgame-modal">
            <h3>Match Complete</h3>
            <div className="pool-endgame-text">
              Winner: <strong>{winnerName}</strong>
            </div>
            <div className="pool-endgame-subtext">
              {roomState.lastEvent === "turn_forfeit"
                ? "Win by forfeit (timeout streak)."
                : roomState.lastEvent === "eight_ball_resolved"
                  ? "8-ball result finalized."
                  : "Game finished."}
            </div>
          </div>
        </div>
      )}

      <div className="pool-table-frame">
        {waitingForOpponent && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(6, 18, 14, 0.42)",
              color: "#f4f8f4",
              fontWeight: 700,
              fontSize: "15px",
              letterSpacing: "0.2px",
              zIndex: 2,
              pointerEvents: "none",
              textShadow: "0 2px 6px rgba(0,0,0,0.45)"
            }}
          >
            {opponentShotInProgress
              ? `${lastShotPlayerName} has shot...`
              : `Waiting for ${currentTurnPlayerName}...`}
          </div>
        )}
        {tournamentInteractionLocked && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(5, 15, 12, 0.68)",
              color: "#f4f8f4",
              fontWeight: 700,
              fontSize: "14px",
              letterSpacing: "0.2px",
              zIndex: 2,
              pointerEvents: "none"
            }}
          >
            {poolSessionState === PoolSessionState.WAITING_FOR_START || poolSessionState === PoolSessionState.LOBBY
              ? "Lobby phase: table is locked"
              : "Match pending: table is locked"}
          </div>
        )}
        <canvas
          ref={canvasRef}
          className="pool-canvas"
          width={WORLD_WIDTH}
          height={WORLD_HEIGHT}
          onPointerMove={handleAimMove}
          onPointerDown={handleAimStart}
          onPointerUp={handleAimRelease}
          onPointerCancel={handleAimRelease}
          onPointerEnter={handleAimMove}
          style={tournamentInteractionLocked ? { pointerEvents: "none" } : undefined}
        />
      </div>
    </div>
  );
}

export default PoolGame;
